import apiClient from './client';

// Use centralized API client as 'api' for consistency
const api = apiClient;

/**
 * Get all expenses with filters
 * @param {Object} params - Query parameters (status, category_id, start_date, end_date, module, etc.)
 * @returns {Promise} API response
 */
export const getExpenses = async (params = {}) => {
  console.log('[Frontend API] getExpenses called with params:', params);
  const response = await api.get('/expenses', { params });
  console.log('[Frontend API] getExpenses response:', response.data);
  return response.data;
};

/**
 * Get a single expense by ID
 * @param {number} id - Expense ID
 * @returns {Promise} API response
 */
export const getExpense = async (id) => {
  const response = await api.get(`/expenses/${id}`);
  return response.data;
};

/**
 * Create a new expense
 * @param {Object} expenseData - Expense data (including optional 'module')
 * @returns {Promise} API response
 */
export const createExpense = async (expenseData) => {
  const response = await api.post('/expenses', expenseData);
  return response.data;
};

/**
 * Update an expense
 * @param {number} id - Expense ID
 * @param {Object} expenseData - Updated expense data
 * @returns {Promise} API response
 */
export const updateExpense = async (id, expenseData) => {
  const response = await api.put(`/expenses/${id}`, expenseData);
  return response.data;
};

/**
 * Approve an expense (Admin only)
 * @param {number} id - Expense ID
 * @returns {Promise} API response
 */
export const approveExpense = async (id) => {
  const response = await api.post(`/expenses/${id}/approve`);
  return response.data;
};

/**
 * Reject an expense (Admin only)
 * @param {number} id - Expense ID
 * @param {string} reason - Rejection reason
 * @returns {Promise} API response
 */
export const rejectExpense = async (id, reason) => {
  const response = await api.post(`/expenses/${id}/reject`, { reason });
  return response.data;
};

/**
 * Post expense to General Ledger (Admin only)
 * @param {number} id - Expense ID
 * @returns {Promise} API response
 */
export const postExpenseToGL = async (id) => {
  const response = await api.post(`/expenses/${id}/post-to-gl`);
  return response.data;
};

/**
 * Delete an expense
 * @param {number} id - Expense ID
 * @returns {Promise} API response
 */
export const deleteExpense = async (id) => {
  const response = await api.delete(`/expenses/${id}`);
  return response.data;
};

/**
 * Get expense approval history
 * @param {number} id - Expense ID
 * @returns {Promise} API response
 */
export const getExpenseHistory = async (id) => {
  const response = await api.get(`/expenses/${id}/history`);
  return response.data;
};

/**
 * Get expense analytics
 * @param {Object} params - Query parameters (start_date, end_date, module)
 * @returns {Promise} API response
 */
export const getExpenseAnalytics = async (params = {}) => {
  const response = await api.get('/expenses/analytics/summary', { params });
  return response.data;
};

/**
 * Upload a receipt image for an expense
 * @param {File} file - The receipt file to upload
 * @returns {Promise} API response with receipt_url
 */
export const uploadReceipt = async (file) => {
  const formData = new FormData();
  formData.append('receipt', file);
  
  const response = await api.post('/expenses/upload-receipt', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Export expense report (download as PDF or CSV)
 * @param {Object} params - Query parameters (format, status, category_id, start_date, end_date, module)
 * @returns {Promise<Blob>} File blob for download
 */
export const exportExpenseReport = async (params = {}) => {
  const response = await api.get('/expenses/report/export', {
    params,
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Send expense report via email
 * @param {Object} data - { recipientEmail, recipientName, format, filters }
 * @returns {Promise} API response
 */
export const emailExpenseReport = async (data) => {
  const response = await api.post('/expenses/report/email', data);
  return response.data;
};

export default {
  getExpenses,
  getExpense,
  createExpense,
  updateExpense,
  approveExpense,
  rejectExpense,
  postExpenseToGL,
  deleteExpense,
  getExpenseHistory,
  getExpenseAnalytics,
  uploadReceipt,
  exportExpenseReport,
  emailExpenseReport
};
